import React, { useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  HistoryOutlined,
  LinkOutlined,
  ReloadOutlined,
  SyncOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  SeasonTicketCodeDto,
  SeasonTicketCodeStatus,
  useAdminLinkSeasonTicket,
  useRevalidateSeasonTicket,
  useSeasonTickets,
  useUnlinkSeasonTicket,
} from '@shared/api/hooks/useSeasonTickets';
import { useUsers } from '@shared/api/hooks/useUsers';
import { useAuditLogs } from '@shared/api/hooks/useAudit';
import { useTenantAdminConfig, useUpdateTenantConfig } from '@shared/api/hooks/useTenantAdminConfig';
import { useAuthStore } from '@shared/hooks/useAuthStore';

const STATUS_LABELS: Record<SeasonTicketCodeStatus, string> = {
  PENDING_VALIDATION: 'In attesa di validazione',
  ACTIVE: 'Attivo',
  REVOKED: 'Revocato',
  UNLINKED: 'Scollegato',
};

const STATUS_COLORS: Record<SeasonTicketCodeStatus, string> = {
  PENDING_VALIDATION: 'orange',
  ACTIVE: 'green',
  REVOKED: 'red',
  UNLINKED: 'default',
};

const STATUS_FILTER_OPTIONS = [
  { label: 'Tutti', value: 'ALL' },
  { label: STATUS_LABELS.PENDING_VALIDATION, value: 'PENDING_VALIDATION' },
  { label: STATUS_LABELS.ACTIVE, value: 'ACTIVE' },
  { label: STATUS_LABELS.REVOKED, value: 'REVOKED' },
  { label: STATUS_LABELS.UNLINKED, value: 'UNLINKED' },
];

/** Maschera il codice tessera (dato sensibile quanto un QR fisico): mostra solo gli ultimi 4 caratteri. */
function maskCode(code: string): string {
  if (code.length <= 4) return code;
  return `${'•'.repeat(Math.min(8, code.length - 4))}${code.slice(-4)}`;
}

/** Colore dell'esito di validazione. OK verde; rifiuti certi rosso; guasti neutri. */
function validationColor(result: string | null): string {
  if (result === 'OK') return 'green';
  if (result === 'NOT_FOUND' || result === 'IS_TICKET' || result === 'INVALID' || result === 'KO')
    return 'red';
  return 'default'; // ERROR / NOT_CONFIGURED / null
}

/**
 * Registro delle operazioni sugli abbonamenti (audit filtrato per entità
 * SeasonTicketCode). Componente separato così la query parte solo quando il
 * Drawer è aperto.
 */
function AuditRegister(): React.ReactElement {
  const { data, isLoading } = useAuditLogs({ entity: 'SeasonTicketCode', limit: 50 });
  return (
    <Table<{ id: string; action: string; createdAt: string; entityId: string; changes: unknown }>
      rowKey="id"
      size="small"
      loading={isLoading}
      dataSource={data?.data ?? []}
      pagination={false}
      columns={[
        {
          title: 'Quando',
          dataIndex: 'createdAt',
          width: 140,
          render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm'),
        },
        {
          title: 'Operazione',
          dataIndex: 'action',
          width: 130,
          render: (a: string) => <Tag>{a}</Tag>,
        },
        {
          title: 'Dettaglio',
          dataIndex: 'changes',
          render: (c: unknown) => (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {c ? JSON.stringify(c) : '—'}
            </Typography.Text>
          ),
        },
      ]}
    />
  );
}

interface UnlinkFormValues {
  reason: string;
}

interface AdminLinkFormValues {
  code: string;
  seat?: string;
}

export function SeasonTicketsPage(): React.ReactElement {
  const [filterStatus, setFilterStatus] = useState<SeasonTicketCodeStatus | 'ALL'>('ALL');
  const [filterSearch, setFilterSearch] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [unlinkId, setUnlinkId] = useState<string | null>(null);
  const [revalidatingId, setRevalidatingId] = useState<string | null>(null);
  const [adminLinkOpen, setAdminLinkOpen] = useState(false);
  const [adminLinkUserSearch, setAdminLinkUserSearch] = useState('');
  const [adminLinkUserId, setAdminLinkUserId] = useState<string | null>(null);

  const [unlinkForm] = Form.useForm<UnlinkFormValues>();
  const [adminLinkForm] = Form.useForm<AdminLinkFormValues>();

  const apiStatus = filterStatus === 'ALL' ? undefined : filterStatus;
  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = useSeasonTickets({ status: apiStatus, search: filterSearch, page, limit: 20 });

  const unlinkMutation = useUnlinkSeasonTicket();
  const revalidateMutation = useRevalidateSeasonTicket();
  const adminLinkMutation = useAdminLinkSeasonTicket();

  // Sganciamento e fine stagione riservati agli ADMIN (il backend rifiuta comunque i non-admin).
  const { user } = useAuthStore();
  const canAdmin = user ? ['CLUB_ADMIN', 'SUPER_ADMIN', 'DEVELOPER'].includes(user.role) : false;

  const [auditOpen, setAuditOpen] = useState(false);

  // Fine stagione = tenant_configs.subscription_ends_at (club-wide, vale per tutti).
  const tenantConfig = useTenantAdminConfig();
  const updateConfig = useUpdateTenantConfig();
  const seasonEndsAt = tenantConfig.data?.subscriptionEndsAt ?? null;

  const onSaveSeasonEnd = async (d: Dayjs | null): Promise<void> => {
    if (!tenantConfig.data) return; // evita di sovrascrivere metadata prima del load
    try {
      await updateConfig.mutateAsync({
        subscriptionEndsAt: d ? d.endOf('day').toISOString() : null,
        // metadata è richiesto dal tipo: round-trip del valore corrente, non lo tocco.
        metadata: (tenantConfig.data.metadata ?? {}) as Record<string, unknown>,
      });
      message.success('Fine stagione aggiornata');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const adminLinkUsersQuery = useUsers({
    role: 'FAN',
    limit: 20,
    search: adminLinkUserSearch.trim() || undefined,
  });
  const adminLinkUserOptions = useMemo(
    () =>
      (adminLinkUsersQuery.data?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.displayName ?? u.email} · ${u.email}`,
      })),
    [adminLinkUsersQuery.data],
  );

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  const onUnlink = async (): Promise<void> => {
    if (!unlinkId) return;
    const v = await unlinkForm.validateFields();
    try {
      await unlinkMutation.mutateAsync({ id: unlinkId, reason: v.reason });
      message.success('Codice scollegato');
      setUnlinkId(null);
      unlinkForm.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onAdminLink = async (): Promise<void> => {
    if (!adminLinkUserId) {
      message.error('Seleziona un tifoso');
      return;
    }
    const v = await adminLinkForm.validateFields();
    try {
      await adminLinkMutation.mutateAsync({
        userId: adminLinkUserId,
        code: v.code,
        seat: v.seat || undefined,
      });
      message.success('Codice associato al tifoso');
      setAdminLinkOpen(false);
      setAdminLinkUserId(null);
      setAdminLinkUserSearch('');
      adminLinkForm.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onRevalidate = async (id: string): Promise<void> => {
    setRevalidatingId(id);
    try {
      await revalidateMutation.mutateAsync(id);
      message.success('Rivalidazione richiesta');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    } finally {
      setRevalidatingId(null);
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Abbonamenti Tessera
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="Cerca tifoso, email o codice"
            style={{ minWidth: 220 }}
            defaultValue={filterSearch}
            onSearch={(v) => {
              setFilterSearch(v || undefined);
              setPage(1);
            }}
          />
          <Select
            style={{ minWidth: 200 }}
            options={STATUS_FILTER_OPTIONS}
            value={filterStatus}
            onChange={(v) => {
              setFilterStatus(v as SeasonTicketCodeStatus | 'ALL');
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button icon={<HistoryOutlined />} onClick={() => setAuditOpen(true)}>
            Registro
          </Button>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => setAdminLinkOpen(true)}
          >
            Associa codice
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Rivendicazione di tessere fisiche stagionali"
        description="Ogni codice (QR/barcode della tessera fisica emessa dalla società esterna) può essere associato a un solo account. Diverso dagli abbonamenti loyalty/shop gestiti altrove."
      />
      <Space style={{ marginBottom: 16 }} align="center">
        <Typography.Text strong>Fine stagione (vale per tutti gli abbonamenti):</Typography.Text>
        {canAdmin ? (
          <DatePicker
            format="DD/MM/YYYY"
            placeholder="Nessuna scadenza"
            value={seasonEndsAt ? dayjs(seasonEndsAt) : null}
            disabled={updateConfig.isPending || tenantConfig.isLoading}
            onChange={onSaveSeasonEnd}
          />
        ) : (
          <Typography.Text>
            {seasonEndsAt ? dayjs(seasonEndsAt).format('DD/MM/YYYY') : '— non impostata —'}
          </Typography.Text>
        )}
      </Space>
      <Table<SeasonTicketCodeDto>
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Utente',
            width: 220,
            render: (_v, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {r.user.displayName ?? <Typography.Text type="secondary">—</Typography.Text>}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {r.user.email}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: 'Codice',
            dataIndex: 'code',
            width: 140,
            render: (v: string) => <code>{maskCode(v)}</code>,
          },
          {
            title: 'Stato',
            dataIndex: 'status',
            width: 160,
            render: (s: SeasonTicketCodeStatus) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>,
          },
          {
            title: 'Posto',
            dataIndex: 'seat',
            width: 160,
            render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Wallet',
            width: 120,
            render: (_v, r) =>
              r.googleWalletLink || r.appleWalletLink ? (
                <Space size={4}>
                  {r.googleWalletLink ? (
                    <Tooltip title="Google Wallet">
                      <a href={r.googleWalletLink} target="_blank" rel="noreferrer">
                        <Tag color="blue" icon={<WalletOutlined />} style={{ margin: 0 }}>
                          G
                        </Tag>
                      </a>
                    </Tooltip>
                  ) : null}
                  {r.appleWalletLink ? (
                    <Tooltip title="Apple Wallet">
                      <a href={r.appleWalletLink} target="_blank" rel="noreferrer">
                        <Tag icon={<WalletOutlined />} style={{ margin: 0 }}>
                          A
                        </Tag>
                      </a>
                    </Tooltip>
                  ) : null}
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Associato il',
            dataIndex: 'linkedAt',
            width: 130,
            render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm'),
          },
          {
            title: 'Ultima validazione',
            width: 190,
            render: (_v, r) => {
              const atRisk =
                r.consecutiveKoDays >= 1 &&
                (r.status === 'ACTIVE' || r.status === 'PENDING_VALIDATION');
              return r.lastValidatedAt ? (
                <Space direction="vertical" size={0}>
                  <Tag color={validationColor(r.lastValidationResult)} style={{ margin: 0 }}>
                    {r.lastValidationResult ?? '—'}
                  </Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {dayjs(r.lastValidatedAt).format('DD/MM/YY HH:mm')}
                  </Typography.Text>
                  {atRisk ? (
                    <Tooltip title="Un altro esito negativo in un giorno diverso e la tessera viene revocata automaticamente.">
                      <Tag color="warning" style={{ margin: '2px 0 0' }}>
                        A rischio revoca ({r.consecutiveKoDays}/2)
                      </Tag>
                    </Tooltip>
                  ) : null}
                </Space>
              ) : (
                <Typography.Text type="secondary">— mai —</Typography.Text>
              );
            },
          },
          {
            title: 'Revocato il',
            dataIndex: 'revokedAt',
            width: 130,
            render: (v: string | null) =>
              v ? (
                <Tooltip title="Marcato dal job notturno di revoca">
                  <Typography.Text type="danger">{dayjs(v).format('DD/MM/YY')}</Typography.Text>
                </Tooltip>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Azioni',
            width: 200,
            align: 'right',
            fixed: 'right',
            render: (_v, r) => (
              <Space>
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  loading={revalidatingId === r.id && revalidateMutation.isPending}
                  disabled={r.status === 'UNLINKED'}
                  onClick={() => onRevalidate(r.id)}
                >
                  Rivalida
                </Button>
                {canAdmin ? (
                  <Button
                    danger
                    size="small"
                    disabled={r.status === 'UNLINKED'}
                    onClick={() => {
                      unlinkForm.resetFields();
                      setUnlinkId(r.id);
                    }}
                  >
                    Scollega
                  </Button>
                ) : null}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="Scollega codice tessera"
        open={unlinkId !== null}
        onCancel={() => setUnlinkId(null)}
        onOk={onUnlink}
        okText="Scollega"
        okButtonProps={{ danger: true }}
        confirmLoading={unlinkMutation.isPending}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Il codice tornerà libero e potrà essere riassociato a un altro account."
        />
        <Form form={unlinkForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Motivo (audit, obbligatorio)"
            rules={[{ required: true, max: 500 }]}
          >
            <Input.TextArea rows={4} placeholder="es. Segnalato come duplicato dal tifoso..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Associa codice tessera a un tifoso"
        open={adminLinkOpen}
        onCancel={() => {
          setAdminLinkOpen(false);
          setAdminLinkUserId(null);
          setAdminLinkUserSearch('');
          adminLinkForm.resetFields();
        }}
        onOk={onAdminLink}
        okText="Associa"
        confirmLoading={adminLinkMutation.isPending}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Uso tipico: il tifoso comunica il codice per telefono/email al supporto invece di scansionarlo dall'app."
        />
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
              Tifoso
            </Typography.Text>
            <AutoComplete
              value={adminLinkUserSearch}
              onChange={(v) => {
                setAdminLinkUserSearch(v);
                setAdminLinkUserId(null);
              }}
              onSelect={(v) => {
                setAdminLinkUserId(v as string);
                const opt = adminLinkUserOptions.find((o) => o.value === v);
                setAdminLinkUserSearch(opt?.label ?? '');
              }}
              options={adminLinkUserOptions}
              style={{ width: '100%' }}
              placeholder="Cerca tifoso per email o nome…"
              allowClear
            />
          </div>
          <Form form={adminLinkForm} layout="vertical">
            <Form.Item
              name="code"
              label="Codice tessera"
              rules={[{ required: true, max: 150 }]}
            >
              <Input placeholder="es. TC-2026-000123" />
            </Form.Item>
            <Form.Item name="seat" label="Posto (opzionale)">
              <Input placeholder="es. Curva Nord - Fila 12 - Posto 34" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Drawer
        title="Registro operazioni abbonamenti"
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        width={640}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Ultime 50 operazioni: associazioni, sganciamenti e revoche automatiche (AUTO_REVOKE dal giro giornaliero)."
        />
        {auditOpen ? <AuditRegister /> : null}
      </Drawer>
    </Card>
  );
}
